import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './location-picker.scss';
import { bindable } from 'aurelia';
import { geocodeAddress, searchAddresses, AddressSuggestion } from '../../services/nominatim-service';

// Fix Leaflet default icon paths broken by bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: new URL('leaflet/dist/images/marker-icon.png', import.meta.url).href,
    iconRetinaUrl: new URL('leaflet/dist/images/marker-icon-2x.png', import.meta.url).href,
    shadowUrl: new URL('leaflet/dist/images/marker-shadow.png', import.meta.url).href,
});

export class LocationPicker {
    @bindable public latitude: number | null = null;
    @bindable public longitude: number | null = null;
    @bindable public address: string = '';

    public isGeocoding: boolean = false;
    public geocodeError: boolean = false;
    public showSuggestions: boolean = false;
    public suggestions: AddressSuggestion[] = [];

    private _map: L.Map = null;
    private _marker: L.Marker = null;
    private _debounceTimer: ReturnType<typeof setTimeout> = null;
    private _suppressNextSearch: boolean = false;
    public container: HTMLElement = null;

    public attached(): void {
        this._initMap();
    }

    public detached(): void {
        if (this._map) {
            this._map.remove();
            this._map = null;
        }
        clearTimeout(this._debounceTimer);
    }

    private _initMap(): void {
        const lat = this.latitude ?? 59.33;
        const lon = this.longitude ?? 18.07;
        const zoom = (this.latitude != null) ? 13 : 5;

        this._map = L.map(this.container).setView([lat, lon], zoom);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            maxZoom: 19,
        }).addTo(this._map);

        if (this.latitude != null && this.longitude != null) {
            this._placeMarker(this.latitude, this.longitude);
        }

        this._map.on('click', (e: L.LeafletMouseEvent) => {
            this.latitude = e.latlng.lat;
            this.longitude = e.latlng.lng;
            this._placeMarker(e.latlng.lat, e.latlng.lng);
            this._closeSuggestions();
        });
    }

    private _placeMarker(lat: number, lon: number): void {
        if (this._marker) {
            this._marker.setLatLng([lat, lon]);
        } else {
            this._marker = L.marker([lat, lon], { draggable: true }).addTo(this._map);
            this._marker.on('dragend', () => {
                const pos = this._marker.getLatLng();
                this.latitude = pos.lat;
                this.longitude = pos.lng;
            });
        }
    }

    public onAddressInput(): void {
        this.geocodeError = false;
        clearTimeout(this._debounceTimer);
        if (!this.address || this.address.trim().length < 2) {
            this._closeSuggestions();
            return;
        }
        if (this._suppressNextSearch) {
            this._suppressNextSearch = false;
            return;
        }
        this._debounceTimer = setTimeout(() => void this._fetchSuggestions(), 300);
    }

    private async _fetchSuggestions(): Promise<void> {
        const query = this.address;
        if (!query || query.trim().length < 2) return;
        const results = await searchAddresses(query);
        // Only update if the address hasn't changed while we were waiting
        if (this.address === query) {
            this.suggestions = results;
            this.showSuggestions = results.length > 0;
        }
    }

    public selectSuggestion(suggestion: AddressSuggestion): void {
        this._suppressNextSearch = true;
        this.address = suggestion.displayName;
        this.latitude = suggestion.lat;
        this.longitude = suggestion.lon;
        this._map.setView([suggestion.lat, suggestion.lon], 13);
        this._placeMarker(suggestion.lat, suggestion.lon);
        this._closeSuggestions();
    }

    public onInputBlur(): void {
        // Delay so click on suggestion fires first
        setTimeout(() => this._closeSuggestions(), 200);
    }

    public onInputFocus(): void {
        if (this.suggestions.length > 0) {
            this.showSuggestions = true;
        }
    }

    private _closeSuggestions(): void {
        this.showSuggestions = false;
    }

    public async geocode(): Promise<void> {
        if (!this.address) return;
        this._closeSuggestions();
        this.isGeocoding = true;
        this.geocodeError = false;
        try {
            const result = await geocodeAddress(this.address);
            if (result) {
                this.latitude = result.lat;
                this.longitude = result.lon;
                this._map?.setView([result.lat, result.lon], 13);
                this._placeMarker(result.lat, result.lon);
            } else {
                this.geocodeError = true;
            }
        } catch {
            this.geocodeError = true;
        } finally {
            // setTimeout pushes into a macrotask so Aurelia's patched timer flushes the view
            setTimeout(() => { this.isGeocoding = false; }, 0);
        }
    }

    public clearCoordinates(): void {
        this.latitude = null;
        this.longitude = null;
        if (this._marker) {
            this._marker.remove();
            this._marker = null;
        }
        this._closeSuggestions();
    }

    public get latFormatted(): string {
        return this.latitude != null ? this.latitude.toFixed(6) : '';
    }

    public get lonFormatted(): string {
        return this.longitude != null ? this.longitude.toFixed(6) : '';
    }
}
